import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { Toaster } from "sonner";

// Layout
import Layout from "./components/layout/Layout";

// Eager pages (entry points)
import Login from "./pages/Login";
import SessionTimeout from "./components/SessionTimeout";

// Lazy-loaded pages (code splitting for faster initial load)
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Manifests = lazy(() => import("./pages/Manifests"));
const Inspections = lazy(() => import("./pages/Inspections"));
const Finance = lazy(() => import("./pages/Finance"));
const FinanceAudit = lazy(() => import("./pages/FinanceAudit"));
const GatePasses = lazy(() => import("./pages/GatePasses"));
const GatePointGatePasses = lazy(() => import("./pages/GatePointGatePasses"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const PublicVerification = lazy(() => import("./pages/PublicVerification"));
const SecuritySettings = lazy(() => import("./pages/SecuritySettings"));
const Reports = lazy(() => import("./pages/Reports"));
const Security = lazy(() => import("./pages/Security"));
const TrashBin = lazy(() => import("./pages/TrashBin"));
const Review = lazy(() => import("./pages/Review"));

// Warm the route chunks in the background after first paint so sidebar navigation is instant
const PAGE_LOADERS = [
  () => import("./pages/Manifests"), () => import("./pages/Inspections"), () => import("./pages/GatePasses"),
  () => import("./pages/Finance"), () => import("./pages/FinanceAudit"), () => import("./pages/Reports"),
  () => import("./pages/Review"), () => import("./pages/AuditLogs"), () => import("./pages/UserManagement"),
  () => import("./pages/Security"), () => import("./pages/SecuritySettings"), () => import("./pages/TrashBin"),
];
if (typeof window !== "undefined" && localStorage.getItem("token")) {
  const warm = () => PAGE_LOADERS.forEach((load, i) => setTimeout(() => load().catch(() => {}), 150 * i));
  if ("requestIdleCallback" in window) window.requestIdleCallback(warm, { timeout: 4000 }); else setTimeout(warm, 2500);
}

const PageLoader = () => (
  <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loader">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm text-slate-500">Loading...</p>
    </div>
  </div>
);

// Landing page component - no portal access from root
const LandingPage = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="text-center">
      <h1 className="text-2xl font-bold text-white mb-4">Somali Bureau of Standards</h1>
      <p className="text-slate-400">This page is not available.</p>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SessionTimeout />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public Routes - Outside /admin */}
            <Route path="/verify/:token" element={<PublicVerification />} />
            
            {/* Admin Login - /admin is the main login entry point */}
            <Route path="/admin" element={<Login />} />
            <Route path="/admin/login" element={<Navigate to="/admin" replace />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />
            
            {/* Protected Admin Routes - /admin/* */}
            <Route path="/admin/dashboard" element={<Layout />}>
              <Route index element={<Dashboard />} />
            </Route>
            <Route path="/admin/manifests" element={<Layout />}>
              <Route index element={<Manifests />} />
            </Route>
            <Route path="/admin/inspections" element={<Layout />}>
              <Route index element={<Inspections />} />
            </Route>
            <Route path="/admin/review" element={<Layout />}>
              <Route index element={<Review />} />
            </Route>
            <Route path="/admin/finance" element={<Layout />}>
              <Route index element={<Finance />} />
            </Route>
            <Route path="/admin/gate-passes" element={<Layout />}>
              <Route index element={<GatePasses />} />
            </Route>
            <Route path="/admin/gate-point-passes" element={<Layout />}>
              <Route index element={<GatePointGatePasses />} />
            </Route>
            <Route path="/admin/users" element={<Layout />}>
              <Route index element={<UserManagement />} />
            </Route>
            <Route path="/admin/audit-logs" element={<Layout />}>
              <Route index element={<AuditLogs />} />
            </Route>
            <Route path="/admin/security-settings" element={<Layout />}>
              <Route index element={<SecuritySettings />} />
            </Route>
            <Route path="/admin/reports" element={<Layout />}>
              <Route index element={<Reports />} />
            </Route>
            <Route path="/admin/finance-audit" element={<Layout />}>
              <Route index element={<FinanceAudit />} />
            </Route>
            <Route path="/admin/security" element={<Layout />}>
              <Route index element={<Security />} />
            </Route>
            <Route path="/admin/trash" element={<Layout />}>
              <Route index element={<TrashBin />} />
            </Route>
            
            {/* Legacy /login redirect */}
            <Route path="/login" element={<Navigate to="/admin" replace />} />
            
            {/* Root path - No portal access */}
            <Route path="/" element={<LandingPage />} />
            
            {/* Catch all - show landing page */}
            <Route path="*" element={<LandingPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
